// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
// Material
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
// RXJS
import { debounceTime, delay, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge, of, Subscription } from 'rxjs';
// Translate Module
import { TranslateService } from '@ngx-translate/core';
// NGRX
import { select, Store } from '@ngrx/store';
// Services
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
// Models
import {
  AuthService,
  currentUserRoleIds,
  RoleTemplate,
  RoleTemplateActionTypes,
  RoleTemplateOnServerDeleted,
  RoleTemplatesDataSource,
  RoleTemplatesOnServerDeleted,
  RoleTemplateOnServerActivated,
  RoleTemplatesPageRequested,
  selectRoleTemplateError,
  selectRoleTemplateLatestSuccessfullAction,
} from '../../../../../core/auth';
import { AppState } from '../../../../../core/reducers';
// Components
import { RoleTemplateEditDialogComponent } from '../role-template-edit/role-template-edit.dialog.component';
// Management components
import { CompaniesService, CompanyModel } from 'src/app/core/management';
import { MatSelect } from '@angular/material/select';
import { utc } from 'moment';
import { DownloadService } from '../../../../../shared/utils/download.service';

@Component({
  selector: 'kt-role-templates-list',
  templateUrl: './role-templates-list.component.html',
  styleUrls: ['./role-templates-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleTemplatesListComponent implements OnInit, OnDestroy {
  // Table fields
  dataSource: RoleTemplatesDataSource;
  displayedColumns = [];
  searchBarColumns = [];
  showSearchBar = false;
  departments$ = this.authService.getTwixorDepartments('ALL');
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  // Filter fields
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchTitleInput') searchTitleInput: ElementRef;
  @ViewChild('searchIsActiveSelect') searchIsActiveSelect: MatSelect;
  getCompanyAutocomplete = this.companiesService.searchCompanies.bind(this.companiesService);
  // Selection
  selection = new SelectionModel<RoleTemplate>(true, []);
  rolesResult: RoleTemplate[] = [];
  companies: CompanyModel[] = [];
  isSuperAdmin: boolean;
  statusTracker = 0;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  /**
   * Component constructor
   *
   * @param store
   * @param dialog
   * @param snackBar
   * @param layoutUtilsService
   * @param companiesService
   * @param cdr
   * @param translate
   * @param authService
   * @param downloadService
   */
  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private authService: AuthService,
    private downloadService: DownloadService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.subscriptions.push(
      this.store.pipe(select(currentUserRoleIds)).subscribe((res) => {
        this.isSuperAdmin = res ? res.includes(1) : null;
        this.displayedColumns = this.isSuperAdmin
          ? ['select', 'title', 'isActive', 'actions']
          : ['select', 'title', 'isActive', 'actions'];
        this.searchBarColumns = this.isSuperAdmin
          ? ['searchSelect', 'searchTitle', 'searchIsActive', 'searchActions']
          : ['searchSelect', 'searchTitle', 'searchIsActive', 'searchActions'];
      }),
    );
    // If the user changes the sort order, reset back to the first page.
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => {
        this.paginator.pageIndex = 0;
        this.sort.active = this.sort.direction ? this.sort.active : 'id';
        this.sort.direction = this.sort.direction || 'desc';
      }),
    );

    /* Data load will be triggered in two cases:
		- when a pagination event occurs => this.paginator.page
		- when a sort event occurs => this.sort.sortChange
		**/
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.loadRoleTemplatesList();
          }),
        )
        .subscribe(),
    );

    // Filtration, bind to searchInput
    this.subscriptions.push(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          // tslint:disable-next-line:max-line-length
          debounceTime(150), // The user can type quite quickly in the input box, and that could trigger a lot of server requests. With this operator, we are limiting the amount of server requests emitted to a maximum of one every 150ms
          distinctUntilChanged(), // This operator will eliminate duplicate values
          tap(() => {
            this.paginator.pageIndex = 0;
            this.loadRoleTemplatesList();
          }),
        )
        .subscribe(),
    );

    // Init DataSource
    this.dataSource = new RoleTemplatesDataSource(this.store);
    this.subscriptions.push(
      this.dataSource.entitySubject.pipe(skip(1), distinctUntilChanged()).subscribe((res) => {
        this.rolesResult = res;
      }),
    );

    // First load
    of(undefined)
      .pipe(take(1), delay(1000))
      .subscribe(() => {
        // Remove this line, just loading imitation
        this.loadRoleTemplatesList();
      });
    this.subscriptions.push(
      this.store.pipe(select(selectRoleTemplateError), skip(1)).subscribe((res) => {
        if (res) {
          this.layoutUtilsService.generalDialog(
            '',
            `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">${
                res.error?.errors[0]?.message || this.translate.instant('GLOBAL.UNSPECIFIED_ERROR')
              }</p>
            </div>
          `,
            '',
            '',
            'Close',
            false,
            true,
            '400px',
          );
        }
      }),
    );
    this.subscriptions.push(
      this.store
        .pipe(select(selectRoleTemplateLatestSuccessfullAction), skip(1))
        .subscribe((res) => {
          if (res && res.action_type) {
            let successMsg;
            let msgType;
            switch (res.action_type) {
              case RoleTemplateActionTypes.RoleTemplateCreated:
                this.loadRoleTemplatesList();
                successMsg = this.translate.instant('ROLE_TEMPLATES.EDIT.ADD_MESSAGE');
                msgType = MessageType.Create;
                break;
              case RoleTemplateActionTypes.RoleTemplateUpdated:
                this.loadRoleTemplatesList();
                successMsg = this.translate.instant('ROLE_TEMPLATES.EDIT.UPDATE_MESSAGE');
                msgType = MessageType.Update;
                break;
              case RoleTemplateActionTypes.RoleTemplatesDeleted:
                this.loadRoleTemplatesList();
                this.selection.clear();
                successMsg = this.translate.instant(
                  'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_MULTI.MESSAGE',
                );
                msgType = MessageType.Delete;
                break;
              case RoleTemplateActionTypes.RoleTemplateDeleted:
                this.loadRoleTemplatesList();
                successMsg = this.translate.instant(
                  'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_SIMPLE.MESSAGE',
                );
                msgType = MessageType.Delete;
                break;
              case RoleTemplateActionTypes.RoleTemplateActivated:
                this.loadRoleTemplatesList();
                successMsg = this.translate.instant(
                  'ROLE_TEMPLATES.ACTIVATE_ROLE_TEMPLATE_SIMPLE.MESSAGE',
                );
                msgType = MessageType.Delete;
                break;
            }
            if (successMsg) {
              this.layoutUtilsService.showActionNotification(
                successMsg,
                msgType,
                3000,
                true,
                false,
              );
            }
          }
        }),
    );
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Load Roles List
   */
  loadRoleTemplatesList() {
    this.selection.clear();
    const queryParams = this.getQueryParams();
    // Call request from server
    this.store.dispatch(new RoleTemplatesPageRequested({ page: queryParams }));
    this.selection.clear();
  }

  getQueryParams() {
    return new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};

    if (this.searchIsActiveSelect?.value === undefined) this.searchIsActiveSelect.value = 'true';

    filter.isActive = this.searchIsActiveSelect?.value;

    const searchText: string = this.searchInput.nativeElement.value;
    filter.title = searchText;
    filter.searchData = searchText;

    filter.searchTitle = this.searchTitleInput?.nativeElement?.value;

    return filter;
  }

  /** ACTIONS */
  /**
   * Delete role
   *
   * @param _item
   */
  deleteRoleTemplate(_item: RoleTemplate) {
    const _title: string = this.translate.instant(
      'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_SIMPLE.TITLE',
    );
    const _description: string = this.translate.instant(
      'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.store.dispatch(new RoleTemplateOnServerDeleted({ id: _item.id }));
    });
  }

  /**
   * Activate role template
   *
   * @param _item
   */
  activateRoleTemplate(_item: RoleTemplate) {
    const _title: string = this.translate.instant(
      'ROLE_TEMPLATES.ACTIVATE_ROLE_TEMPLATE_SIMPLE.TITLE',
    );
    const _description: string = this.translate.instant(
      'ROLE_TEMPLATES.ACTIVATE_ROLE_TEMPLATE_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'ROLE_TEMPLATES.ACTIVATE_ROLE_TEMPLATE_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.store.dispatch(new RoleTemplateOnServerActivated({ id: _item.id }));
    });
  }

  /**
   * Delete roles
   *
   */
  deleteRoleTemplates() {
    const _title: string = this.translate.instant(
      'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_MULTI.TITLE',
    );
    const _description: string = this.translate.instant(
      'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_MULTI.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'ROLE_TEMPLATES.DELETE_ROLE_TEMPLATE_MULTI.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      const ids: number[] = [];
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < this.selection.selected.length; i++) {
        ids.push(this.selection.selected[i].id);
      }
      this.store.dispatch(new RoleTemplatesOnServerDeleted({ ids }));
    });
  }

  /**
   * Fetch selected rows
   */
  fetchRoleTemplates() {
    const messages = [];
    this.selection.selected.forEach((elem) => {
      messages.push({
        text: `${elem.title}`,
        id: elem.id.toString(),
        // status: elem.username
      });
    });
    this.layoutUtilsService.fetchElements(messages);
  }

  /**
   * Add role template
   */
  addRoleTemplate() {
    const newRole = new RoleTemplate();
    newRole.clear(); // Set all defaults fields
    this.editRoleTemplate(newRole);
  }

  /**
   * Edit role template
   *
   * @param role
   */
  editRoleTemplate(roleTemplate: RoleTemplate) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.maxHeight = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.width = '100vw';
    this.dialog.open(RoleTemplateEditDialogComponent, {
      data: {
        roleTemplateId: roleTemplate.id,
        roleTemplateTitle: roleTemplate.title,
        queryParams: this.getQueryParams(),
      },
    });
  }

  /**
   * Check all rows are selected
   */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.rolesResult.length;
    return numSelected === numRows;
  }

  /**
   * Toggle selection
   */
  masterToggle() {
    if (this.selection.selected.length === this.rolesResult.length) {
      this.selection.clear();
    } else {
      this.rolesResult.forEach((row) => this.selection.select(row));
    }
  }

  /**
   * Retursn CSS Class Name by status
   *
   * @param is_active: boolean
   */
  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  /**
   * Returns Item Status in string
   * @param is_active: boolean
   */
  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect.value = 'true';
        break;
      case 1:
        this.searchIsActiveSelect.value = 'false';
        break;
      case 2:
        this.searchIsActiveSelect.value = '';
        break;
    }

    this.reloadRoleTemplatesList();
  }

  reloadRoleTemplatesList(): void {
    this.paginator.pageIndex = 0;

    this.loadRoleTemplatesList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchTitleInput.nativeElement.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadRoleTemplatesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchTitleInput.nativeElement.focus());
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadRoleTemplatesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadRoleTemplatesList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect.value) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  exportRolesAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.authService
      .getRolesAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `Roles List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }
}
